import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { collection, query, where, limit as firestoreLimit, getDocs } from "firebase/firestore";
import { db } from "@itemgo/core";
import { Search, User, Package, Ticket, X } from "lucide-react";

interface Result {
  id: string;
  kind: "user" | "booking" | "ticket";
  title: string;
  subtitle: string;
  path: string;
}

/**
 * Global command palette — press Cmd/Ctrl+K from anywhere in the portal to
 * jump to a user, booking or ticket by name/ID.
 */
export default function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Result[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState(0);

  // Search term (plain — no extra timer needed)
  const debouncedQ = q;

  useEffect(() => {
    if (!open) return;
    setQ("");
    setResults([]);
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  useEffect(() => {
    if (!open || debouncedQ.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const term = debouncedQ.trim().toLowerCase();
    (async () => {
      try {
        const out: Result[] = [];

        // Users (displayName or email prefix)
        const usersSnap = await getDocs(
          query(collection(db, "users"), where("displayName", ">=", term), where("displayName", "<=", term + "\uf8ff"), firestoreLimit(6))
        );
        usersSnap.docs.forEach((d) => {
          const data = d.data();
          out.push({
            id: d.id,
            kind: "user",
            title: data.displayName || "Unnamed",
            subtitle: `${data.email || ""} · ${data.role || ""}`,
            path: `/users/${d.id}`,
          });
        });

        // Tickets (subject/title contains)
        const ticketSnap = await getDocs(query(collection(db, "tickets"), firestoreLimit(30)));
        ticketSnap.docs
          .filter((d) => ((d.data().subject || d.data().title || "") as string).toLowerCase().includes(term))
          .slice(0, 5)
          .forEach((d) => {
            const data = d.data();
            out.push({
              id: d.id,
              kind: "ticket",
              title: (data.subject || data.title || d.id.slice(0, 12)) as string,
              subtitle: `Ticket · ${data.status || ""}`,
              path: "/tickets",
            });
          });

        // Bookings (id prefix)
        const bookingSnap = await getDocs(query(collection(db, "bookings"), firestoreLimit(100)));
        bookingSnap.docs
          .filter((d) => d.id.toLowerCase().startsWith(term) || (d.data().customerId || "").toString().toLowerCase().startsWith(term))
          .slice(0, 5)
          .forEach((d) => {
            const data = d.data();
            out.push({
              id: d.id,
              kind: "booking",
              title: `Booking ${d.id.slice(0, 10)}`,
              subtitle: `Booking · ${data.status || ""}`,
              path: `/bookings/${d.id}`,
            });
          });

        setResults(out.slice(0, 12));
        setSelected(0);
      } catch (e) {
        console.warn("[CommandPalette] search error:", e);
        setResults([]);
      } finally {
        setSearching(false);
      }
    })();
  }, [debouncedQ, open]);

  if (!open) return null;

  const go = (r: Result) => {
    navigate(r.path);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, results.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    }
    if (e.key === "Enter" && results[selected]) go(results[selected]);
  };

  const icons = {
    user: <User size={14} className="text-brand-blue shrink-0" />,
    booking: <Package size={14} className="text-brand-orange shrink-0" />,
    ticket: <Ticket size={14} className="text-amber-600 shrink-0" />,
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-surface-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-surface-border">
          <Search size={16} className="text-text-tertiary" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search users, bookings, tickets…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-text-tertiary"
          />
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={16} />
          </button>
        </div>
        <div className="max-h-[40vh] overflow-y-auto py-1">
          {searching && <p className="px-4 py-3 text-xs text-text-tertiary">Searching…</p>}
          {!searching && q.trim().length < 2 && (
            <p className="px-4 py-3 text-xs text-text-tertiary">Type at least 2 characters to search.</p>
          )}
          {!searching && q.trim().length >= 2 && results.length === 0 && (
            <p className="px-4 py-3 text-xs text-text-tertiary">No results.</p>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.kind}-${r.id}`}
              onClick={() => go(r)}
              onMouseEnter={() => setSelected(i)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                i === selected ? "bg-brand-blue/10" : ""
              }`}
            >
              {icons[r.kind]}
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-text-primary truncate">{r.title}</span>
                <span className="block text-[11px] text-text-tertiary truncate">{r.subtitle}</span>
              </span>
              <span className="text-[10px] uppercase tracking-wide text-text-tertiary">{r.kind}</span>
            </button>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-surface-border text-[11px] text-text-tertiary flex items-center gap-3">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
